import React from 'react';
import { cn } from '@express-labs/raven-tools';
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  Slider,
} from 'pure-react-carousel';
import IconLeft from '../IconLeft';
import IconRight from '../IconRight';
import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../../shared';
import ImageWatcher from '../ImageWatcher';
import s from '../../Carousel.scss';
import RenderSlide from '../RenderSlide';

export type CarouselBasicWithNavProps = {
  cards?: never;
  /** Moves the nth slide into the leftmost position of the carousel */
  currentSlide?: number;
  /** Switch the applied css rules and change the component rendering based on the device. */
  deviceType?: 'desktop' | 'mobile';
  /** array of image url strings */
  largeImages?: string[];
  /** When true, Pure React Carousel will display a master spinner over the entire carousel until all carousel images have loaded. */
  loading?: boolean;
  /** array of image url strings */
  mainImages?: string[];
  /** If modal prop is true, clicking on a slide opens a modal window */
  modal?: boolean;
  /** The maximum number of slides to show in the carousel at one time */
  visibleSlides?: number;
  /** When true, mouse hovers or finger presses on images in the carousel will cause them to zoom-in. */
  zoom?: boolean;
};

export type CarouselBasicWithNavPropsInjectedByCarousel = {
  /** Is the Carousel component currently showing a modal window.  Passed in by Carousel. */
  isModalOpen?: boolean;
  /** The index number of an image from the images array currently visible in the modal.  Passed
  in by Carousel. */
  modalImageIndex?: number | null;
  onClick?: (i: number, ev: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (i: number, ev: React.KeyboardEvent<HTMLElement>) => void;
  /**  a unique id for the carousel. This number is used to help create unique css id's and class
  names so that you can have more than one carousel on the page and style them differently,
  or reference a dom element via their unique id attribute name via javascript.
  This number is generated automatically in Carousel and passed in. */
  uid: number;
};

class CarouselBasicWithNav extends React.Component<
  CarouselBasicWithNavProps & CarouselBasicWithNavPropsInjectedByCarousel
> {
  render() {
    const {
      currentSlide = 0,
      deviceType = 'desktop',
      isModalOpen = false,
      largeImages = [],
      loading = false,
      mainImages = [],
      modalImageIndex = null,
      onClick,
      onKeyDown,
      uid,
      visibleSlides = 1,
      zoom = true,
    } = this.props;

    return (
      <CarouselProvider
        className={cn(
          s.container,
          s.basicContainerWithNav,
          'carousel--basic-with-nav'
        )}
        currentSlide={currentSlide}
        data-testid="CarouselBasicWithNav"
        data-uid={uid}
        hasMasterSpinner={loading}
        lockOnWindowScroll
        naturalSlideHeight={SLIDE_HEIGHT}
        naturalSlideWidth={SLIDE_WIDTH}
        totalSlides={mainImages.length}
        visibleSlides={visibleSlides}
      >
        <Slider>
          <ImageWatcher images={mainImages} />
          {mainImages.map((mainImage, i) => (
            <RenderSlide
              deviceType={deviceType}
              i={i}
              isModalOpen={isModalOpen}
              key={largeImages[i]}
              large={largeImages[i]}
              loading={loading}
              main={mainImages[i]}
              modalImageIndex={modalImageIndex}
              onClick={onClick}
              onKeyDown={onKeyDown}
              uid={uid}
              zoom={zoom}
            />
          ))}
        </Slider>
        <ButtonBack className={s.buttonBack}>
          <IconLeft />
        </ButtonBack>
        <ButtonNext className={s.buttonNext}>
          <IconRight />
        </ButtonNext>
      </CarouselProvider>
    );
  }
}

export default CarouselBasicWithNav;
